import React, { useState, useEffect, useCallback, useContext } from 'react';
import ModalEliminacion from '../modal/ModalEliminacion';
import FilterEscaneoModal from '../modal/FilterEscaneoModal';
import { Get_use_type_name, Truncar_dispositivo, DateTime_to_string } from '../Comun';
import { AuthContext } from '../contexts/AuthContext';
import { apiFetch } from '../utils/apiFetch';

interface Scan
{
    id: number;
    client_id: number;
    event_name: string;
    code: string;
    response: string;
    type_name: string;
    tag: string;
    device: string;
    accepted: boolean;
    use_type: number;
    dt_use: string;
    id_provider: number;
}

interface Client
{
    id: number;
    name: string;
}

/*
 * Configuración de las columnas de la tabla.
 *
 * width     = ancho inicial
 * minWidth  = ancho mínimo permitido
 * maxWidth  = ancho máximo permitido
 */
const TABLE_COLUMNS = [
    { key: 'client', label: 'Recinto', width: 150, minWidth: 120, maxWidth: 450 },
    { key: 'event', label: 'Evento', width: 190, minWidth: 150, maxWidth: 600 },
    { key: 'code', label: 'Código', width: 170, minWidth: 120, maxWidth: 500 },
    { key: 'type', label: 'Tipo', width: 150, minWidth: 120, maxWidth: 400 },
    { key: 'tag', label: 'Tag', width: 150, minWidth: 120, maxWidth: 400 },
    { key: 'response', label: 'Respuesta', width: 220, minWidth: 150, maxWidth: 700 },
    { key: 'device', label: 'Dispositivo', width: 180, minWidth: 150, maxWidth: 500 },
    { key: 'result', label: 'Resultado', width: 150, minWidth: 130, maxWidth: 300 },
    { key: 'datetime', label: 'Fecha y hora', width: 180, minWidth: 160, maxWidth: 350 },
    { key: 'actions', label: '', width: 145, minWidth: 145, maxWidth: 220 }
] as const;

type ColumnKey = typeof TABLE_COLUMNS[number]['key'];

type ColumnWidths = Record<ColumnKey, number>;

const COLUMN_WIDTHS_STORAGE_KEY = 'escaneos-table-column-widths';

/**
 * Carga los anchos guardados anteriormente.
 *
 * Si no hay nada guardado, utiliza los valores definidos
 * en TABLE_COLUMNS.
 */
const getInitialColumnWidths = (): ColumnWidths =>
{
    const defaults = TABLE_COLUMNS.reduce((acc, column) =>
    {
        acc[column.key] = column.width;
        return acc;
    }, {} as ColumnWidths);

    try
    {
        const saved = localStorage.getItem(COLUMN_WIDTHS_STORAGE_KEY);

        if (!saved)
            return defaults;

        const parsed = JSON.parse(saved);

        if (!parsed || typeof parsed !== 'object')
            return defaults;

        const result = { ...defaults };

        TABLE_COLUMNS.forEach(column =>
        {
            const value = parsed[column.key];

            if (typeof value === 'number' && Number.isFinite(value))
            {
                result[column.key] = Math.min(
                    Math.max(value, column.minWidth),
                    column.maxWidth
                );
            }
        });

        return result;
    }
    catch
    {
        return defaults;
    }
};

const Escaneos: React.FC = () =>
{
    const { token } = useContext(AuthContext);

    const [scans, setScans] = useState<Scan[]>([]);
    const [clients, setClients] = useState<Client[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedScan, setSelectedScan] = useState<Scan | null>(null);
    const [isUnregisterByFilter, setUnregisterByFilter] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalResults, setTotalResults] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [expandedScanIndex, setExpandedScanIndex] = useState<number | null>(null);

    /*
     * Anchos actuales de las columnas.
     */
    const [columnWidths, setColumnWidths] = useState<ColumnWidths>(
        getInitialColumnWidths
    );

    /*
     * Información temporal del resize que se está realizando.
     */
    const [resizingColumn, setResizingColumn] = useState<ColumnKey | null>(null);

    const [resizeStart, setResizeStart] = useState<{
        column: ColumnKey;
        startX: number;
        startWidth: number;
    } | null>(null);

    const [filters, setFilters] = useState<{
        id_client: number | null;
        id_event: number | null;
        is_root_response: boolean;
        response: string | null;
        code: string | null;
        type: number | null;
        id_tag: number | null;
        accepted: number | null;
        start_dt: string | null;
        end_dt: string | null;
    }>({
        id_client: null,
        id_event: null,
        is_root_response: false,
        response: null,
        code: null,
        type: null,
        id_tag: null,
        accepted: null,
        start_dt: null,
        end_dt: null
    });

    //-----------------------------------------------------------
    // REDIMENSIONADO DE COLUMNAS
    //-----------------------------------------------------------

    /**
     * Guarda los anchos en localStorage cada vez que cambian.
     */
    useEffect(() =>
    {
        try
        {
            localStorage.setItem(
                COLUMN_WIDTHS_STORAGE_KEY,
                JSON.stringify(columnWidths)
            );
        }
        catch
        {
            // Si localStorage no está disponible, simplemente continuamos.
        }
    }, [columnWidths]);

    /**
     * Comienza el redimensionado de una columna.
     */
    const handleResizeStart = (
        event: React.MouseEvent<HTMLDivElement>,
        columnKey: ColumnKey
    ) =>
    {
        event.preventDefault();
        event.stopPropagation();

        const column = TABLE_COLUMNS.find(
            item => item.key === columnKey
        );

        if (!column)
            return;

        setResizingColumn(columnKey);

        setResizeStart({
            column: columnKey,
            startX: event.clientX,
            startWidth: columnWidths[columnKey]
        });
    };

    /**
     * Mientras se arrastra el borde, modificamos el ancho.
     */
    useEffect(() =>
    {
        if (!resizeStart)
            return;

        const handleMouseMove = (event: MouseEvent) =>
        {
            const column = TABLE_COLUMNS.find(
                item => item.key === resizeStart.column
            );

            if (!column)
                return;

            const difference = event.clientX - resizeStart.startX;

            const newWidth = Math.min(
                Math.max(
                    resizeStart.startWidth + difference,
                    column.minWidth
                ),
                column.maxWidth
            );

            setColumnWidths(prev => ({
                ...prev,
                [resizeStart.column]: newWidth
            }));
        };

        const handleMouseUp = () =>
        {
            setResizeStart(null);
            setResizingColumn(null);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        /*
         * Evita seleccionar texto de la tabla mientras estamos
         * arrastrando una columna.
         */
        document.body.classList.add('is-resizing-table-column');

        return () =>
        {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.classList.remove('is-resizing-table-column');
        };
    }, [resizeStart]);

    /**
     * Restablece todos los anchos a sus valores originales.
     */
    const handleResetColumnWidths = () =>
    {
        const defaults = TABLE_COLUMNS.reduce((acc, column) =>
        {
            acc[column.key] = column.width;
            return acc;
        }, {} as ColumnWidths);

        setColumnWidths(defaults);
    };

    //-----------------------------------------------------------

    const fetchScans = useCallback(async (page: number = 1) =>
    {
        setLoading(true);

        try
        {
            const queryParams = new URLSearchParams({
                page: page.toString(),
                ...(filters.id_client && {
                    id_client: filters.id_client.toString()
                }),
                ...(filters.id_event && {
                    id_event: filters.id_event.toString()
                }),
                ...(filters.code && {
                    code: filters.code
                }),
                ...(filters.response && {
                    [filters.is_root_response ? 'root_response' : 'response']:
                        filters.response
                }),
                ...(filters.type !== null && {
                    type: filters.type.toString()
                }),
                ...(filters.id_tag !== null && {
                    tag: filters.id_tag.toString()
                }),
                ...(filters.accepted !== null && {
                    accepted: filters.accepted.toString()
                }),
                ...(filters.start_dt && {
                    start_dt: filters.start_dt
                }),
                ...(filters.end_dt && {
                    end_dt: filters.end_dt
                })
            });

            const response = await apiFetch(
                `web/search_registered_codes?${queryParams.toString()}`,
                {
                    method: 'GET'
                }
            );

            if (response.ok)
            {
                const data = await response.json();

                setScans(data.results);
                setTotalResults(data.total_results);
                setTotalPages(data.total_pages);
                setCurrentPage(page);
            }
            else
            {
                throw new Error('Error al cargar escaneos.');
            }
        }
        catch (error)
        {
            const errorMsg =
                error instanceof Error
                    ? error.message
                    : 'Error desconocido';

            setError(errorMsg);
        }
        finally
        {
            setLoading(false);
        }
    }, [filters, token]);

    //-----------------------------------------------------------

    const fetchClients = useCallback(async () =>
    {
        try
        {
            const response = await apiFetch(
                `web/get_clients`,
                {
                    method: 'GET'
                }
            );

            if (response.ok)
            {
                const data = await response.json();
                setClients(data);
            }
            else
            {
                throw new Error('Error al cargar clientes.');
            }
        }
        catch (error)
        {
            const errorMsg =
                error instanceof Error
                    ? error.message
                    : 'Error desconocido';

            setError(errorMsg);
        }
    }, [token]);

    //-----------------------------------------------------------

    useEffect(() =>
    {
        const loadInitialData = async () =>
        {
            try
            {
                await fetchClients();
            }
            catch
            {
                setError('Error al cargar datos iniciales.');
            }
        };

        loadInitialData();
    }, [fetchClients]);

    useEffect(() =>
    {
        fetchScans(currentPage);
    }, [fetchScans, currentPage, filters]);

    //-----------------------------------------------------------

    const i_Unregister_selected_code = async () =>
    {
        const formData = new URLSearchParams();

        formData.append(
            'id',
            selectedScan?.id.toString() || ''
        );

        const response = await apiFetch(
            `web/unregister_code`,
            {
                method: 'POST',
                headers: {
                    'Content-Type':
                        'application/x-www-form-urlencoded'
                },
                body: formData.toString()
            }
        );

        if (response.ok)
        {
            fetchScans(1);
        }
        else
        {
            throw new Error(
                'Error al desregistar el código.'
            );
        }
    };

    //-----------------------------------------------------------

    const i_Unregister_codes_by_filter = async () =>
    {
        const queryParams = new URLSearchParams({
            ...(filters.id_client && {
                id_client: filters.id_client.toString()
            }),
            ...(filters.id_event && {
                id_event: filters.id_event.toString()
            }),
            ...(filters.code && {
                code: filters.code
            }),
            ...(filters.response && {
                [filters.is_root_response
                    ? 'root_response'
                    : 'response']: filters.response
            }),
            ...(filters.type !== null && {
                type: filters.type.toString()
            }),
            ...(filters.id_tag !== null && {
                tag: filters.id_tag.toString()
            }),
            ...(filters.accepted !== null && {
                accepted: filters.accepted.toString()
            }),
            ...(filters.start_dt && {
                start_dt: filters.start_dt
            }),
            ...(filters.end_dt && {
                end_dt: filters.end_dt
            })
        });

        const response = await apiFetch(
            `web/unregister_codes_by_filter?${queryParams.toString()}`,
            {
                method: 'GET'
            }
        );

        if (response.ok)
        {
            fetchScans(1);
        }
        else
        {
            throw new Error(
                'Error al desregistar códigos.'
            );
        }
    };

    //-----------------------------------------------------------

    const handleConfirmUnregister = async () =>
    {
        setIsModalOpen(false);

        try
        {
            if (isUnregisterByFilter)
            {
                await i_Unregister_codes_by_filter();
            }
            else
            {
                await i_Unregister_selected_code();
            }
        }
        catch (error)
        {
            const errorMsg =
                error instanceof Error
                    ? error.message
                    : 'Error desconocido';

            setError(errorMsg);
        }
    };

    const handleCancelUnregister = () =>
    {
        setIsModalOpen(false);
    };

    const handleUnregisterClick = (scan: Scan) =>
    {
        setUnregisterByFilter(false);
        setSelectedScan(scan);
        setIsModalOpen(true);
    };

    const handleUnregisterFilteredClick = () =>
    {
        setUnregisterByFilter(true);
        setIsModalOpen(true);
    };

    const handleFilterOpen = () =>
    {
        setIsFilterModalOpen(true);
    };

    //-----------------------------------------------------------

    const handleApplyFilters = (newFilters: {
        id_client: number | null;
        id_event: number | null;
        is_root_response: boolean;
        response: string | null;
        code: string | null;
        type: number | null;
        id_tag: number | null;
        accepted: number | null;
        start_dt: string | null;
        end_dt: string | null;
    }) =>
    {
        setFilters(newFilters);
        setCurrentPage(1);
    };

    const handleClearFilters = () =>
    {
        setFilters({
            id_client: null,
            id_event: null,
            is_root_response: false,
            response: null,
            code: null,
            type: null,
            id_tag: null,
            accepted: null,
            start_dt: null,
            end_dt: null
        });

        setCurrentPage(1);
    };

    const handlePageChange = (newPage: number) =>
    {
        if (
            newPage > 0 &&
            newPage <= totalPages
        )
        {
            setCurrentPage(newPage);
        }
    };

    const handleToggleExpand = (index: number) =>
    {
        setExpandedScanIndex(prev =>
            prev === index ? null : index
        );
    };

    const Get_client_name = (id: number): string =>
    {
        const client = clients.find(
            client => client.id === id
        );

        return client
            ? client.name
            : '<desconocido>';
    };

    //-----------------------------------------------------------

    const i_Message_warning_unregister = () =>
    {
        if (isUnregisterByFilter)
        {
            return (
                <p>
                    Los escaneos mostrados van a ser borrados
                </p>
            );
        }

        return (
            <p>
                El ÚLTIMO REGISTRO del código{' '}
                <b>{selectedScan?.code}</b>{' '}
                va a ser borrado
            </p>
        );
    };

    //-----------------------------------------------------------

    const handleDownloadFilteredClick = async () =>
    {
        const queryParams = new URLSearchParams({
            ...(filters.id_client && {
                id_client: filters.id_client.toString()
            }),
            ...(filters.id_event && {
                id_event: filters.id_event.toString()
            }),
            ...(filters.code && {
                code: filters.code
            }),
            ...(filters.response && {
                [filters.is_root_response
                    ? 'root_response'
                    : 'response']: filters.response
            }),
            ...(filters.type !== null && {
                type: filters.type.toString()
            }),
            ...(filters.id_tag !== null && {
                tag: filters.id_tag.toString()
            }),
            ...(filters.accepted !== null && {
                accepted: filters.accepted.toString()
            }),
            ...(filters.start_dt && {
                start_dt: filters.start_dt
            }),
            ...(filters.end_dt && {
                end_dt: filters.end_dt
            })
        });

        try
        {
            const response = await apiFetch(
                `web/export_search_registered_codes?${queryParams.toString()}`,
                {
                    method: 'GET'
                }
            );

            if (!response.ok)
            {
                throw new Error(
                    'Error al descargar el archivo CSV.'
                );
            }

            const blob = await response.blob();

            const url =
                window.URL.createObjectURL(blob);

            const a =
                document.createElement('a');

            a.href = url;
            a.download = 'export.csv';

            document.body.appendChild(a);

            a.click();

            document.body.removeChild(a);

            window.URL.revokeObjectURL(url);
        }
        catch (error)
        {
            const errorMsg =
                error instanceof Error
                    ? error.message
                    : 'Error desconocido';

            setError(errorMsg);
        }
    };

    //-----------------------------------------------------------

    /**
     * Devuelve el ancho de una columna para utilizarlo
     * tanto en el <colgroup> como en el resto de la tabla.
     */
    const getColumnWidth = (columnKey: ColumnKey) =>
    {
        return columnWidths[columnKey];
    };

    //-----------------------------------------------------------

    return (
        <div className="eventos-container">
            <div className="miguitas">
                Escaneos
            </div>

            <div className="content-wrapper">

                {/* ── Toolbar ── */}
                <div
                    className="cod-toolbar"
                    style={{ justifyContent: 'flex-end' }}
                >
                    <div className="cod-toolbar__right">

                        <button
                            className="icon-btn"
                            onClick={handleFilterOpen}
                        >
                            <svg
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                            >
                                <path d="M3 6h18M7 12h10M11 18h2" />
                            </svg>

                            Filtro
                        </button>

                        <button
                            className="icon-btn"
                            onClick={handleDownloadFilteredClick}
                        >
                            <svg
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                            >
                                <path d="M12 3v13M7 11l5 5 5-5M3 21h18" />
                            </svg>

                            Descargar filtrados
                        </button>

                        <button
                            className="icon-btn icon-btn--danger"
                            onClick={handleUnregisterFilteredClick}
                        >
                            <svg
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                            >
                                <path d="M3 10h13a5 5 0 0 1 0 10H7" />
                                <path d="M6 6l-3 4 3 4" />
                            </svg>

                            Desescanear filtrados
                        </button>

                        {/* Restablecer anchos */}
                        <button
                            className="icon-btn table-reset-widths-btn"
                            onClick={handleResetColumnWidths}
                            title="Restablecer el ancho de las columnas"
                        >
                            <svg
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                            >
                                <path d="M3 12a9 9 0 1 0 3-6.7" />
                                <path d="M3 4v6h6" />
                            </svg>

                            Restablecer columnas
                        </button>

                    </div>
                </div>

                {/* ── Errores ── */}
                {error && (
                    <p className="error-message">
                        {error}
                    </p>
                )}

                {/* ══ DESKTOP: tabla ══ */}
                <div className="codes-container desktop-only">

                    {loading && !error && (
                        <p className="loading-message">
                            Obteniendo escaneos...
                        </p>
                    )}

                    {!loading &&
                        !error &&
                        scans.length === 0 && (
                            <p>
                                No hay escaneos para mostrar.
                            </p>
                        )}

                    {!loading &&
                        !error &&
                        scans.length > 0 && (

                            <div className="table-scroll table-scroll--resizable">

                                <table className="eventos-table eventos-table--resizable">

                                    {/* 
                                     * El colgroup hace que el ancho definido
                                     * para cada columna se aplique de forma
                                     * consistente a toda la tabla.
                                     */}
                                    <colgroup>
                                        {TABLE_COLUMNS.map(column => (
                                            <col
                                                key={column.key}
                                                style={{
                                                    width: `${getColumnWidth(column.key)}px`
                                                }}
                                            />
                                        ))}
                                    </colgroup>

                                    <thead>
                                        <tr>

                                            {TABLE_COLUMNS.map(column => (

                                                <th
                                                    key={column.key}
                                                    className={
                                                        resizingColumn === column.key
                                                            ? 'table-column--resizing'
                                                            : ''
                                                    }
                                                >

                                                    {column.label}

                                                    {/* 
                                                     * No ponemos resize handle
                                                     * en la última columna porque
                                                     * es la de acciones.
                                                     */}
                                                    {column.key !== 'actions' && (
                                                        <div
                                                            className={`table-column-resizer ${
                                                                resizingColumn === column.key
                                                                    ? 'table-column-resizer--active'
                                                                    : ''
                                                            }`}
                                                            onMouseDown={(event) =>
                                                                handleResizeStart(
                                                                    event,
                                                                    column.key
                                                                )
                                                            }
                                                            title="Arrastrar para cambiar el ancho"
                                                        />
                                                    )}

                                                </th>

                                            ))}

                                        </tr>
                                    </thead>

                                    <tbody>

                                        {scans.map((scan, index) =>
                                        {
                                            const formattedDateTime =
                                                DateTime_to_string(
                                                    new Date(scan.dt_use)
                                                );

                                            return (
                                                <tr key={index}>

                                                    {/* Recinto */}
                                                    <td
                                                        className="cod-muted-cell table-resizable-cell"
                                                        title={Get_client_name(scan.client_id)}
                                                    >
                                                        {Get_client_name(scan.client_id)}
                                                    </td>

                                                    {/* Evento */}
                                                    <td
                                                        className="cod-muted-cell table-resizable-cell"
                                                        title={scan.event_name}
                                                    >
                                                        {scan.event_name}
                                                    </td>

                                                    {/* Código */}
                                                    <td
                                                        className="cod-code-cell table-resizable-cell"
                                                        title={scan.code}
                                                    >
                                                        {scan.code}
                                                    </td>

                                                    {/* Tipo */}
                                                    <td
                                                        className="cod-muted-cell table-resizable-cell"
                                                        title={scan.type_name}
                                                    >
                                                        {scan.type_name}
                                                    </td>

                                                    {/* Tag */}
                                                    <td
                                                        className="cod-muted-cell table-resizable-cell"
                                                        title={scan.tag}
                                                    >
                                                        {scan.tag}
                                                    </td>

                                                    {/* Respuesta */}
                                                    <td
                                                        className="cod-muted-cell table-resizable-cell"
                                                        title={scan.response}
                                                    >
                                                        {scan.response}
                                                    </td>

                                                    {/* Dispositivo */}
                                                    <td
                                                        className="cod-muted-cell table-resizable-cell"
                                                        title={scan.device}
                                                    >
                                                        {Truncar_dispositivo(scan.device)}
                                                    </td>

                                                    {/* Resultado */}
                                                    <td className="cod-muted-cell">

                                                        <span
                                                            className={
                                                                scan.accepted
                                                                    ? 'ev-pill ev-pill--green'
                                                                    : 'ev-pill ev-pill--red'
                                                            }
                                                        >
                                                            {Get_use_type_name(
                                                                scan.use_type
                                                            )}
                                                        </span>

                                                    </td>

                                                    {/* Fecha */}
                                                    <td
                                                        className="cod-muted-cell table-resizable-cell"
                                                        title={formattedDateTime}
                                                    >
                                                        {formattedDateTime}
                                                    </td>

                                                    {/* Acciones */}
                                                    <td className="evento-actions">

                                                        <button
                                                            className="icon-btn icon-btn--unscan"
                                                            onClick={() =>
                                                                handleUnregisterClick(
                                                                    scan
                                                                )
                                                            }
                                                            disabled={!scan.accepted}
                                                        >
                                                            <svg
                                                                width="13"
                                                                height="13"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2.5"
                                                            >
                                                                <path d="M3 10h13a5 5 0 0 1 0 10H7" />
                                                                <path d="M6 6l-3 4 3 4" />
                                                            </svg>

                                                            Desescanear
                                                        </button>

                                                    </td>

                                                </tr>
                                            );
                                        })}

                                    </tbody>

                                </table>

                            </div>
                        )}

                </div>

                {/* ══ MOBILE: cards colapsables ══ */}
                <div className="mobile-only">

                    {loading && !error && (
                        <p className="loading-message">
                            Obteniendo escaneos...
                        </p>
                    )}

                    {!loading &&
                        !error &&
                        scans.length === 0 && (
                            <p>
                                No hay escaneos para mostrar.
                            </p>
                        )}

                    {!loading &&
                        !error &&
                        scans.length > 0 && (

                            <div className="scan-cards">

                                {scans.map((scan, index) =>
                                {
                                    const formattedDateTime =
                                        DateTime_to_string(
                                            new Date(scan.dt_use)
                                        );

                                    const isOpen =
                                        expandedScanIndex === index;

                                    return (
                                        <div
                                            key={index}
                                            className="scan-card"
                                        >

                                            <div
                                                className="scan-card__header"
                                                onClick={() =>
                                                    handleToggleExpand(index)
                                                }
                                            >
                                                <span
                                                    className={
                                                        `scan-dot ${
                                                            scan.accepted
                                                                ? 'scan-dot--ok'
                                                                : 'scan-dot--err'
                                                        }`
                                                    }
                                                />

                                                <span className="scan-card__code">
                                                    {scan.code}
                                                </span>

                                                <span className="scan-card__date">
                                                    {formattedDateTime}
                                                </span>

                                                <span
                                                    className={
                                                        `scan-badge ${
                                                            scan.accepted
                                                                ? 'scan-badge--ok'
                                                                : 'scan-badge--err'
                                                        }`
                                                    }
                                                >
                                                    {Get_use_type_name(
                                                        scan.use_type
                                                    )}
                                                </span>

                                                <svg
                                                    width="14"
                                                    height="14"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2.5"
                                                    className={
                                                        `scan-chevron ${
                                                            isOpen
                                                                ? 'scan-chevron--open'
                                                                : ''
                                                        }`
                                                    }
                                                >
                                                    <path d="M6 9l6 6 6-6" />
                                                </svg>
                                            </div>

                                            {isOpen && (
                                                <>
                                                    <div className="scan-card__detail">

                                                        <div className="scan-detail-row">
                                                            <span className="scan-detail-row__label">
                                                                Código
                                                            </span>

                                                            <span className="scan-detail-row__val">
                                                                {scan.code}
                                                            </span>
                                                        </div>

                                                        <div className="scan-detail-row">
                                                            <span className="scan-detail-row__label">
                                                                Recinto
                                                            </span>

                                                            <span className="scan-detail-row__val">
                                                                {Get_client_name(
                                                                    scan.client_id
                                                                )}
                                                            </span>
                                                        </div>

                                                        <div className="scan-detail-row">
                                                            <span className="scan-detail-row__label">
                                                                Evento
                                                            </span>

                                                            <span className="scan-detail-row__val">
                                                                {scan.event_name}
                                                            </span>
                                                        </div>

                                                        <div className="scan-detail-row">
                                                            <span className="scan-detail-row__label">
                                                                Tipo
                                                            </span>

                                                            <span className="scan-detail-row__val">
                                                                {scan.type_name}
                                                            </span>
                                                        </div>

                                                        <div className="scan-detail-row">
                                                            <span className="scan-detail-row__label">
                                                                Tag
                                                            </span>

                                                            <span className="scan-detail-row__val">
                                                                {scan.tag}
                                                            </span>
                                                        </div>

                                                        <div className="scan-detail-row">
                                                            <span className="scan-detail-row__label">
                                                                Respuesta
                                                            </span>

                                                            <span className="scan-detail-row__val">
                                                                {scan.response}
                                                            </span>
                                                        </div>

                                                        <div className="scan-detail-row">
                                                            <span className="scan-detail-row__label">
                                                                Dispositivo
                                                            </span>

                                                            <span
                                                                className="scan-detail-row__val"
                                                                title={scan.device}
                                                            >
                                                                {Truncar_dispositivo(
                                                                    scan.device
                                                                )}
                                                            </span>
                                                        </div>

                                                    </div>

                                                    <div className="scan-card__footer">

                                                        <button
                                                            className="icon-btn icon-btn--danger scan-unscan-btn"
                                                            onClick={() =>
                                                                handleUnregisterClick(
                                                                    scan
                                                                )
                                                            }
                                                            disabled={!scan.accepted}
                                                        >
                                                            <svg
                                                                width="13"
                                                                height="13"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2.5"
                                                            >
                                                                <path d="M3 10h13a5 5 0 0 1 0 10H7" />
                                                                <path d="M6 6l-3 4 3 4" />
                                                            </svg>

                                                            Desescanear
                                                        </button>

                                                    </div>
                                                </>
                                            )}

                                        </div>
                                    );
                                })}

                            </div>
                        )}

                </div>

                {/* ── Paginación ── */}
                {!loading && scans.length > 0 && (

                    <div className="pagination-controls">

                        <div className="pagination-buttons">

                            <button
                                onClick={() =>
                                    handlePageChange(
                                        currentPage - 1
                                    )
                                }
                                disabled={currentPage <= 1}
                            >
                                <i className="fas fa-caret-left" />
                            </button>

                            <span>
                                {currentPage.toLocaleString('es-ES')}
                                {' / '}
                                {totalPages.toLocaleString('es-ES')}
                            </span>

                            <button
                                onClick={() =>
                                    handlePageChange(
                                        currentPage + 1
                                    )
                                }
                                disabled={
                                    currentPage >= totalPages
                                }
                            >
                                <i className="fas fa-caret-right" />
                            </button>

                        </div>

                        <div className="pagination-info">
                            <span>
                                {totalResults.toLocaleString('es-ES')}
                                {' resultados'}
                            </span>
                        </div>

                    </div>
                )}

            </div>

            {/* ── Modal de eliminación ── */}
            {isModalOpen && (

                <ModalEliminacion
                    isOpen={isModalOpen}
                    onClose={handleCancelUnregister}
                    onConfirm={handleConfirmUnregister}
                >
                    {i_Message_warning_unregister()}
                </ModalEliminacion>

            )}

            {/* ── Modal de filtros ── */}
            {isFilterModalOpen && (

                <FilterEscaneoModal
                    isOpen={isFilterModalOpen}
                    onClose={() =>
                        setIsFilterModalOpen(false)
                    }
                    onApplyFilters={handleApplyFilters}
                    onClearFilters={handleClearFilters}
                    clients={clients}
                    currentFilters={filters}
                />

            )}

        </div>
    );
};

export default Escaneos;